import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useOverflow } from "@/hooks/use-overflow";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";

/** 业务系统透传的一条扩展信息：键 + 原样的值。 */
export type ExtendedInfoEntry = readonly [key: string, value: unknown];

/** 默认先露几条。透传字段是排障时才细看的东西，不该霸占属性栏。 */
const DEFAULT_VISIBLE_ENTRIES = 3;

/**
 * 解析业务系统透传的 `extended_info`。对象按键值展开；服务端已保证是对象，
 * 但历史数据和手工改库都可能不是，所以标量与解析失败都有兜底，不让详情页整片挂掉。
 */
export function parseExtendedInfo(value: string | null | undefined, valueLabel: string, rawLabel: string): ExtendedInfoEntry[] {
  if (!value?.trim()) return [];

  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.entries(parsed as Record<string, unknown>);
    }
    return [[valueLabel, parsed]];
  } catch {
    return [[rawLabel, value]];
  }
}

export function isExtendedInfoJsonEmpty(value: string | null | undefined): boolean {
  // value 为空、空对象、空数组都算空。
  if (!value?.trim()) return true;
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed === null) return true;
    if (typeof parsed === "object") {
      if (Array.isArray(parsed)) return parsed.length === 0;
      return Object.keys(parsed).length === 0;
    }
    return false;
  } catch {
    return false;
  }
}

/** 业务系统只传键名不传标签，把 camelCase / snake_case 拆成词是唯一能做的可读化。 */
export function friendlyKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

/** 取出某个 `extended_info` 的键值对；标签文案由这里统一取，省得每个调用点都串一遍 i18n。 */
export function useExtendedInfo(value: string | null | undefined): ExtendedInfoEntry[] {
  const { t } = useI18n();
  return parseExtendedInfo(value, t("extendedInfo.value"), t("extendedInfo.raw"));
}

export type ExtendedInfoListProps = {
  className?: string;
  entries: readonly ExtendedInfoEntry[];
  visibleCount?: number;
  border?: boolean;
};

/**
 * 扩展信息的统一呈现。工单、客户、关联业务对象三处透传都走这里——它们本就是同一种东西，
 * 各写各的只会让同一份 JSON 在三个页面长得不一样。
 *
 * 排版取紧凑的「左键右值」行，而不是一条一个卡片：字段是业务系统随手传的，条数不可控，
 * 每条占一个带边框的块会把三分之一宽的属性栏撑成几屏，且信息密度极低。
 */
export function ExtendedInfoList({ className, entries, visibleCount = DEFAULT_VISIBLE_ENTRIES, border = false }: ExtendedInfoListProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  if (entries.length === 0) return null;

  const isCollapsible = entries.length > visibleCount;
  const visible = isCollapsible && !expanded ? entries.slice(0, visibleCount) : entries;

  return (
    <div className={cn("min-w-0", className)}>
      {/* 行挨得密，0ms 会在鼠标扫过时连着弹一串黑框，给个短延迟。 */}
      <TooltipProvider delayDuration={300}>
        <dl className={cn("px-1.5", border && "border rounded-lg px-3")}>
          {visible.map(([key, value]) => (
            <ExtendedInfoRow key={key} label={friendlyKey(key) || key} rawKey={key} value={value} />
          ))}
        </dl>
      </TooltipProvider>
      {isCollapsible ? (
        <Button
          className="mt-1 h-6 px-1.5 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
          size="xs"
          type="button"
          variant="ghost"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? t("extendedInfo.showLess") : t("extendedInfo.showAll", { count: entries.length })}
        </Button>
      ) : null}
    </div>
  );
}

function ExtendedInfoRow({ label, rawKey, value }: { label: string; rawKey: string; value: unknown }) {
  const { t } = useI18n();
  const [valueRef, isClamped] = useOverflow<HTMLElement>();

  // 嵌套对象/数组没法塞进一行，单独占整行的代码块；这类值本来也少见。
  if (value !== null && typeof value === "object") {
    return (
      <div className="border-b py-2 last:border-0">
        <dt className="truncate text-xs text-muted-foreground" title={rawKey}>
          {label}
        </dt>
        <dd className="mt-1.5">
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-2 font-mono text-xs leading-5">
            {JSON.stringify(value, null, 2)}
          </pre>
        </dd>
      </div>
    );
  }

  const text = formatScalar(value, t("extendedInfo.booleanTrue"), t("extendedInfo.booleanFalse"));

  const valueNode = (
    <dd className="min-w-0 flex-1 wrap-break-word text-sm line-clamp-1" ref={valueRef} tabIndex={isClamped ? 0 : undefined}>
      {text}
    </dd>
  );

  return (
    <div className="flex items-baseline gap-3 border-b py-2 last:border-0">
      <dt className="w-1/3 max-w-40 shrink-0 truncate text-xs text-muted-foreground" title={rawKey}>
        {label}
      </dt>
      {isClamped ? (
        <Tooltip>
          <TooltipTrigger asChild>{valueNode}</TooltipTrigger>
          <TooltipContent className="wrap-break-word whitespace-pre-wrap">{text}</TooltipContent>
        </Tooltip>
      ) : (
        valueNode
      )}
    </div>
  );
}

function formatScalar(value: unknown, booleanTrue: string, booleanFalse: string): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? booleanTrue : booleanFalse;
  return String(value);
}
